/**
 * C10's payroll routes (design §5.8), mounted by P11.
 *
 * `/payroll/service-charge/schemes` is declared **before** `/payroll/service-charge/pools/:id`
 * for readability only — the `\d+` constraint and the distinct `pools/` segment already
 * make them unambiguous, and relying on ordering alone is how `new` once got parsed as
 * an id in this app.
 *
 * Every route names the permission guarding the screen's primary **read**. The Statutory
 * rules screen names `payroll.statutory_rule.read` and there is no write code to name:
 * §6.1 registers none, because a statutory rule is not locally overridable.
 */

import type { RouteRecordRaw } from 'vue-router'

import { PAYROLL_C10_PERMISSIONS } from '@/modules/payroll/permissionsC10'
import { HR_C10_PERMISSIONS } from '@/modules/hr/permissionsC10'

export const PAYROLL_C10_ROUTES: RouteRecordRaw[] = [
  {
    /* Pay groups are `internal/hr`'s endpoint and payroll's screen — §5.8 puts them
       beside the runs they drive, which is where somebody looks for them. */
    path: '/payroll/pay-groups',
    name: 'payroll-pay-groups',
    component: () => import('@/modules/payroll/PayGroupsView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.payGroups',
      permission: HR_C10_PERMISSIONS.payGroupRead,
    },
  },
  {
    path: '/payroll/wage-bases',
    name: 'payroll-wage-bases',
    component: () => import('@/modules/payroll/WageBasesView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.wageBases',
      permission: PAYROLL_C10_PERMISSIONS.wageBaseRead,
    },
  },
  {
    path: '/payroll/statutory-rules',
    name: 'payroll-statutory-rules',
    component: () => import('@/modules/payroll/StatutoryRulesView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.statutoryRules',
      permission: PAYROLL_C10_PERMISSIONS.statutoryRuleRead,
    },
  },
  {
    path: '/payroll/statutory-mappings',
    name: 'payroll-statutory-mappings',
    component: () => import('@/modules/payroll/StatutoryMappingsView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.statutoryMappings',
      permission: PAYROLL_C10_PERMISSIONS.statutoryMappingRead,
    },
  },
  {
    path: '/payroll/retro',
    name: 'payroll-retro',
    component: () => import('@/modules/payroll/RetroEventsView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.retro',
      permission: PAYROLL_C10_PERMISSIONS.retroRead,
    },
  },
  {
    path: '/payroll/service-charge',
    name: 'payroll-service-charge',
    component: () => import('@/modules/payroll/service-charge/PoolsListView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.serviceCharge',
      permission: PAYROLL_C10_PERMISSIONS.svchargeRead,
    },
  },
  {
    path: '/payroll/service-charge/schemes',
    name: 'payroll-service-charge-schemes',
    component: () => import('@/modules/payroll/service-charge/SchemesView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.serviceChargeSchemes',
      permission: PAYROLL_C10_PERMISSIONS.svchargeRead,
    },
  },
  {
    path: '/payroll/service-charge/pools/:id(\\d+)',
    name: 'payroll-service-charge-pool',
    component: () => import('@/modules/payroll/service-charge/PoolView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.serviceChargePool',
      permission: PAYROLL_C10_PERMISSIONS.svchargeRead,
    },
  },
  {
    path: '/payroll/returns',
    name: 'payroll-returns',
    component: () => import('@/modules/payroll/returns/StatutoryReturnsListView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.returns',
      permission: PAYROLL_C10_PERMISSIONS.statutoryReturnRead,
    },
  },
  {
    path: '/payroll/returns/:id(\\d+)',
    name: 'payroll-return',
    component: () => import('@/modules/payroll/returns/StatutoryReturnView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.return',
      permission: PAYROLL_C10_PERMISSIONS.statutoryReturnRead,
    },
  },
  {
    path: '/payroll/year-end',
    name: 'payroll-year-end',
    component: () => import('@/modules/payroll/returns/YearEndStatementsView.vue'),
    meta: {
      titleKey: 'payroll.routeTitles.yearEnd',
      permission: PAYROLL_C10_PERMISSIONS.yearEndRead,
    },
  },
]
